/**
 * Ollama LLM Provider - v13 Section 6.10-6.11
 *
 * Provider for local LLM inference via Ollama.
 *
 * Key features:
 * - 100% local inference (zero cost)
 * - Zero data retention (runs locally)
 * - Wide model support (Llama, Mistral, Qwen, etc.)
 * - OpenAI-compatible API format
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 */
import { BaseLLMProvider, LLMProviderType } from './base';
/**
 * Ollama LLM Provider - Local inference
 */
export class OllamaProvider extends BaseLLMProvider {
    constructor(config) {
        super(config);
        this.baseUrl = config.baseUrl ?? 'http://localhost:11434';
        // Model should be explicitly configured via config.model or request.model
        // Empty default will cause error if no model specified - this is intentional
        this.defaultModel = config.model ?? '';
        this.defaultMaxTokens = config.maxTokens ?? 4096;
        this.defaultTemperature = config.temperature ?? 0.7;
    }
    getProviderType() {
        return LLMProviderType.OLLAMA;
    }
    /**
     * Get supported models
     * @deprecated Use getInstalledModels() for local Ollama models
     */
    getSupportedModels() {
        // No hardcoded model list - use getInstalledModels() to query local Ollama
        return [];
    }
    async isAvailable() {
        try {
            const response = await fetch(`${this.baseUrl}/api/tags`);
            return response.ok;
        }
        catch (error) {
            this.logger.debug(`Ollama availability check failed: ${error}`);
            return false;
        }
    }
    /**
     * Get locally installed models
     */
    async getInstalledModels() {
        try {
            const response = await fetch(`${this.baseUrl}/api/tags`);
            if (!response.ok) {
                return [];
            }
            const data = await response.json();
            return data.models?.map((m) => m.name) ?? [];
        }
        catch (error) {
            this.logger.debug(`Failed to get installed models: ${error}`);
            return [];
        }
    }
    async complete(request) {
        const model = request.model ?? this.defaultModel;
        const maxTokens = request.maxTokens ?? this.defaultMaxTokens;
        const temperature = request.temperature ?? this.defaultTemperature;
        // Validate request
        const errors = this.validateRequest(request);
        if (errors.length > 0) {
            return this.createErrorResponse(errors.join('; '), model);
        }
        // Format messages
        const messages = request.messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
        }));
        this.logger.info(`Using model: ${model} for Ollama request`);
        const startTime = Date.now();
        try {
            const response = await fetch(`${this.baseUrl}/api/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    model,
                    messages,
                    stream: false,
                    options: {
                        num_predict: maxTokens,
                        temperature,
                    },
                }),
            });
            if (!response.ok) {
                const error = await response.text();
                throw new Error(`Ollama API error: ${response.status} ${error}`);
            }
            const data = await response.json();
            const processingTime = (Date.now() - startTime) / 1000;
            const content = data.message?.content ?? '';
            // Ollama provides token counts
            const inputTokens = data.prompt_eval_count ?? 0;
            const outputTokens = data.eval_count ?? 0;
            const totalTokens = inputTokens + outputTokens;
            this.logger.debug('Received response from Ollama', {
                model,
                content_length: content.length,
                processing_time: processingTime,
                tokens_used: totalTokens,
            });
            return {
                content,
                model,
                usage: {
                    inputTokens,
                    outputTokens,
                    totalTokens,
                    costUsd: 0, // Local inference is free
                },
                finishReason: data.done ? 'stop' : 'stop',
            };
        }
        catch (error) {
            const processingTime = (Date.now() - startTime) / 1000;
            // Check if Ollama is not running
            if (error instanceof TypeError && error.message.includes('fetch')) {
                const errorMsg = 'Ollama is not running. Start it with: ollama serve';
                this.logger.error('Ollama not available', { error: errorMsg });
                return this.createErrorResponse(errorMsg, model);
            }
            const errorMsg = `Ollama API error: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error('Ollama generation failed', { model, error: errorMsg, processingTime });
            return this.createErrorResponse(errorMsg, model);
        }
    }
}
